import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { CheckCircle2, Calendar, XCircle, Clock, AlertCircle } from 'lucide-react';
import '../../styles-wa/analytics_view.css';
import { getAnalyticsStats, getCampaignDetail, getAnalyticsContactMessages, getMessagePricingStats, getMessagePricingDetail } from '../../api-wa';
import { apiClient } from '../../api-wa';


// ─── Colour palette helpers ───────────────────────────────────────────────────
const PALETTE = [
  ['#6366f1', '#4f46e5'], ['#10b981', '#059669'], ['#f59e0b', '#d97706'],
  ['#ef4444', '#dc2626'], ['#0891b2', '#0e7490'], ['#ec4899', '#db2777'],
  ['#8b5cf6', '#7c3aed'], ['#14b8a6', '#0d9488'],
];
function avatarGrad(i) {
  const [a, b] = PALETTE[i % PALETTE.length];
  return { background: `linear-gradient(135deg, ${a}, ${b})` };
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
function pct(num, den) { return den > 0 ? Math.round((num / den) * 100) : 0; }
function fmt(n) { return (n ?? 0).toLocaleString(); }
function fmtTime(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  } catch { return '—'; }
}
function fmtDate(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString([], { day: '2-digit', month: 'short' }) + ' ' + fmtTime(iso);
  } catch { return '—'; }
}
function getStatusClass(s) {
  const v = (s || '').toLowerCase();
  if (v === 'read') return 'completed';
  if (v === 'delivered') return 'active';
  if (v === 'failed') return 'failed';
  if (v === 'sent' || v === 'waiting') return 'paused';
  return 'paused';
}

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function Skeleton({ h = 20, r = 8 }) {
  return (
    <div style={{ height: h, borderRadius: r, background: 'linear-gradient(90deg, #f0f0f0 25%, #f8f8f8 50%, #f0f0f0 75%)', backgroundSize: '200% 100%', animation: 'shimmer 1.5s infinite' }} />
  );
}

function AnalyticsBanner({ activeSessions = 6, totalSent = 16, overallDelivery = 75 }) {
  return (
    <div style={{ background: 'linear-gradient(to right, #4f46e5, #0ea5e9)', borderRadius: '24px', padding: '32px 40px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', color: '#fff', marginBottom: '24px', boxShadow: '0 10px 25px -5px rgba(14, 165, 233, 0.4)' }}>
      <div>
        <h1 style={{ margin: 0, fontSize: '32px', fontWeight: 800, letterSpacing: '-0.02em', marginBottom: '12px' }}>Communication Intelligence</h1>
        <div style={{ fontSize: '14px', opacity: 0.9, fontWeight: 500 }}>
          Realtime analytics · Delivery tracking · Customer engagement insights · Updated {new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
        </div>
      </div>
      <div style={{ display: 'flex', gap: '48px', textAlign: 'center' }}>
        <div>
          <div style={{ fontSize: '36px', fontWeight: 800, lineHeight: 1 }}>{activeSessions}</div>
          <div style={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.05em', opacity: 0.8, marginTop: '8px' }}>ACTIVE SESSIONS</div>
        </div>
        <div>
          <div style={{ fontSize: '36px', fontWeight: 800, lineHeight: 1 }}>{totalSent}</div>
          <div style={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.05em', opacity: 0.8, marginTop: '8px' }}>TOTAL SENT</div>
        </div>
        <div>
          <div style={{ fontSize: '36px', fontWeight: 800, lineHeight: 1 }}>{overallDelivery}%</div>
          <div style={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.05em', opacity: 0.8, marginTop: '8px' }}>OVERALL DELIVERY</div>
        </div>
      </div>
    </div>
  );
}

function KpiCard({ label, value, icon, color, trend, trendDir, progress, spark, onClick }) {
  const isUp = trendDir === 'up';
  const isDown = trendDir === 'down';

  const colorMap = {
    purple: '#6366f1',
    blue: '#0ea5e9',
    green: '#10b981',
    red: '#ef4444',
    amber: '#f59e0b',
    pink: '#ec4899',
  };
  const activeColor = colorMap[color] || colorMap.purple;

  const trendClass = isUp ? 'up' : isDown ? 'down' : 'neutral';
  const trendSymbol = isUp ? '▲ ' : isDown ? '▼ ' : '';

  return (
    <div 
      className={`an-kpi-card ${color}`} 
      onClick={onClick}
      style={{ cursor: onClick ? 'pointer' : 'default' }}
    >
      <div className="an-kpi-top">
        <div className={`an-kpi-icon ${color}`}>
          {icon}
        </div>
        <div className={`an-kpi-trend ${trendClass}`}>
          {trendSymbol}{trend}
        </div>
      </div>
      
      <div className="an-kpi-value">{value}</div>
      <div className="an-kpi-label">{label}</div>



      <div style={{ marginTop: 'auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div className="an-kpi-progress" style={{ flex: 1, margin: 0 }}>
            <div 
              className="an-kpi-progress-fill" 
              style={{ 
                width: `${Math.min(Math.max(progress !== undefined ? progress : 0, 0), 100)}%`,
                background: activeColor 
              }} 
            />
          </div>
          <span style={{ fontSize: '11px', fontWeight: '800', color: 'var(--text-muted)', width: '32px', textAlign: 'right' }}>
            {progress !== undefined ? `${Math.round(progress)}%` : '0%'}
          </span>
        </div>
      </div>
    </div>
  );
}

function DonutChart({ segments, size = 100 }) {
  const cx = size / 2;
  const cy = size / 2;
  const r = (size - 20) / 2;
  const total = segments.reduce((sum, s) => sum + s.value, 0) || 1;
  let acc = 0;

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      {segments.map((seg, i) => {
        if (seg.value === 0) return null;
        if (seg.value === total) {
          return <circle key={i} cx={cx} cy={cy} r={r} fill={seg.color} />;
        }
        const pct1 = acc / total;
        const pct2 = (acc + seg.value) / total;
        acc += seg.value;
        const a1 = pct1 * 2 * Math.PI - Math.PI / 2;
        const a2 = pct2 * 2 * Math.PI - Math.PI / 2;
        const x1 = cx + r * Math.cos(a1);
        const y1 = cy + r * Math.sin(a1);
        const x2 = cx + r * Math.cos(a2);
        const y2 = cy + r * Math.sin(a2);
        const largeArc = pct2 - pct1 > 0.5 ? 1 : 0;
        const path = `M ${cx} ${cy} L ${x1} ${y1} A ${r} ${r} 0 ${largeArc} 1 ${x2} ${y2} Z`;
        return <path key={i} d={path} fill={seg.color} />;
      })}
      <circle cx={cx} cy={cy} r={r - 10} fill="var(--surface-white)" />
    </svg>
  );
}

function LineChart({ data, colors, height = 100 }) {
  return (
    <div style={{ height, display: 'flex', alignItems: 'flex-end', gap: 4, position: 'relative' }}>
      {data[0]?.values.map((_, i) => {
        return (
          <div key={i} style={{ flex: 1, display: 'flex', alignItems: 'flex-end', justifyContent: 'center', gap: 2, height: '100%' }}>
            {data.map((series, j) => {
              const val = series.values[i];
              // use a reasonable max relative to all values in the series to scale
              const max = Math.max(...series.values, 10);
              const pct = Math.min((val / max) * 100, 100);
              return (
                <div key={j} style={{ width: '100%', maxWidth: 12, height: `${pct}%`, background: colors[j] || '#ccc', borderRadius: '4px 4px 0 0' }} title={`${series.label}: ${val}`} />
              );
            })}
          </div>
        );
      })}
    </div>
  );
}

// ─── Campaign Detail Drawer ───────────────────────────────────────────────────
function CampaignDetailModal({ campaign, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!campaign?.id) { setLoading(false); return; }
    setLoading(true);
    getCampaignDetail(campaign.id)
      .then(res => { setData(res); setLoading(false); })
      .catch(e => { setError(e.message || 'Failed to load'); setLoading(false); });
  }, [campaign?.id]);

  useEffect(() => {
    const h = (e) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onClose]);

  const campInfo = data?.campaign || campaign;
  const messages = data?.messages || [];

  const sent = messages.length;
  const delivered = messages.filter(m => ['Delivered', 'Read'].includes(m.status)).length;
  const read = messages.filter(m => m.status === 'Read').length;
  const failed = messages.filter(m => m.status === 'Failed').length;
  const dRate = pct(delivered, sent);
  const rRate = pct(read, delivered);

  return createPortal(
    <div className="wa-module">
      <div className="an-modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
        <div className="an-modal" style={{ maxWidth: '800px', height: 'auto', maxHeight: '85vh' }}>
        <div className="an-modal-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <div className="an-customer-avatar" style={{ ...avatarGrad(campaign?.id || 1), width: 44, height: 44, borderRadius: 14, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, fontWeight: 800, color: '#fff', flexShrink: 0 }}>
              {(campInfo?.name || 'C')[0]}
            </div>
            <div>
              <div className="an-modal-title">{campInfo?.name || 'Campaign'}</div>
              <div style={{ fontSize: 12.5, color: 'var(--text-muted)', fontWeight: 500, marginTop: 2 }}>
                {campInfo?.status || 'Active'} · {fmtDate(campInfo?.created_at)}
              </div>
            </div>
          </div>
          <button className="close-x" onClick={onClose}>×</button>
        </div>

        <div className="an-modal-body" style={{ flexDirection: 'column', overflow: 'auto', padding: '16px 20px', gap: 16 }}>
          {loading ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div className="an-modal-kpi-row">{[0, 1, 2, 3].map(i => <Skeleton key={i} h={80} r={14} />)}</div>
              <Skeleton h={280} r={14} />
            </div>
          ) : error ? (
            <div className="an-empty"><div className="an-empty-title">Error: {error}</div></div>
          ) : (
            <>
              {/* KPI Row */}
              <div className="an-modal-kpi-row">
                {[
                  { label: 'Total Sent', val: fmt(sent), color: '#3b82f6' },
                  { label: 'Delivered', val: fmt(delivered), color: '#10b981' },
                  { label: 'Read', val: fmt(read), color: '#6366f1' },
                  { label: 'Failed', val: fmt(failed), color: '#ef4444' },
                ].map((k, i) => (
                  <div key={i} className="an-modal-kpi">
                    <div className="an-modal-kpi-val" style={{ color: k.color }}>{k.val}</div>
                    <div className="an-modal-kpi-label">{k.label}</div>
                  </div>
                ))}
              </div>

              {/* Rates */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                {[
                  { label: 'Delivery Rate', rate: dRate, color: '#10b981' },
                  { label: 'Read Rate', rate: rRate, color: '#6366f1' },
                ].map((bar, i) => (
                  <div key={i} style={{ background: 'var(--surface-white)', borderRadius: 14, padding: '16px 18px', border: '1px solid var(--border-color)' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                      <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--text-gray)' }}>{bar.label}</span>
                      <span style={{ fontSize: 14, fontWeight: 800, color: bar.color }}>{bar.rate}%</span>
                    </div>
                    <div className="an-kpi-progress">
                      <div className="an-kpi-progress-fill" style={{ width: `${bar.rate}%`, background: bar.color }} />
                    </div>
                  </div>
                ))}
              </div>

              {/* Message history */}
              <div style={{ background: 'var(--surface-white)', borderRadius: 16, border: '1px solid var(--border-color)', overflow: 'hidden' }}>
                <div style={{ padding: '16px 22px', borderBottom: '1px solid var(--border-color)', display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ fontSize: 13.5, fontWeight: 800, color: 'var(--text-dark)' }}>Recipients & Status</span>
                  <span style={{ fontSize: 12, color: 'var(--text-muted)', fontWeight: 600 }}>{messages.length} messages</span>
                </div>
                <div style={{ maxHeight: 360, overflowY: 'auto' }}>
                  {messages.length === 0 ? (
                    <div className="an-empty"><div className="an-empty-title">No messages in campaign</div></div>
                  ) : messages.slice().reverse().map((m, i) => (
                    <div key={m.id || i} style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '12px 22px', borderBottom: '1px solid var(--border-color)' }}>
                      <div style={{ width: 30, height: 30, borderRadius: 8, background: 'rgba(99,102,241,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#6366f1" strokeWidth="2"><line x1="22" y1="2" x2="11" y2="13" /><polygon points="22 2 15 22 11 13 2 9 22 2" /></svg>
                      </div>
                      <div style={{ flex: 1, minWidth: 0, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div>
                          <div style={{ fontSize: 13, color: 'var(--text-dark)', fontWeight: 600 }}>
                            {m.contact?.name || m.contact?.phone_number || m.recipient || 'Contact'}
                          </div>
                          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
                            <span>{fmtDate(m.timestamp || m.created_at)}</span>
                          </div>
                        </div>
                        <div className={`an-status-badge ${getStatusClass(m.status)}`} style={{ padding: '4px 10px', fontSize: 11 }}>
                          <span className="dot" /> {m.status}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
    </div>,
    document.body
  );
}

// ─── Contact Detail Drawer ─────────────────────────────────────────────────────
function ContactDetailModal({ contact, onClose }) {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!contact?.id) { setLoading(false); return; }
    setLoading(true);
    getAnalyticsContactMessages(contact.id)
      .then(msgs => { setMessages(msgs); setLoading(false); })
      .catch(e => { setError(e.message || 'Failed to load'); setLoading(false); });
  }, [contact?.id]);

  useEffect(() => {
    const h = (e) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onClose]);

  const outgoing = messages.filter(m => m.direction === 'OUTGOING');
  const incoming = messages.filter(m => m.direction === 'INCOMING');
  const delivered = outgoing.filter(m => ['Delivered', 'Read'].includes(m.status)).length;
  const readCount = outgoing.filter(m => m.status === 'Read').length;
  const dRate = pct(delivered, outgoing.length);
  const rRate = pct(readCount, delivered);

  const rawPhone = contact?.phone || contact?.phone_number || '';
  let cleanPhone = rawPhone.replace(/\D/g, '');
  if (cleanPhone.length > 10) cleanPhone = cleanPhone.slice(-10);
  let rawName = contact?.name && contact.name !== 'Unknown' ? contact.name : '';
  let nameDigits = rawName.replace(/\D/g, '');
  const hasRealName = rawName && nameDigits.length < 8;
  const primaryText = hasRealName ? rawName : (cleanPhone || rawPhone || 'Unknown');
  const secondaryText = hasRealName ? (cleanPhone || rawPhone) : '';
  const avatarChar = hasRealName ? rawName.charAt(0).toUpperCase() : (cleanPhone.charAt(0) || '?');

  return createPortal(
    <div className="wa-module">
      <div className="an-modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
        <div className="an-modal" style={{ maxWidth: '800px', height: 'auto', maxHeight: '85vh' }}>
        <div className="an-modal-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <div className="an-customer-avatar" style={{ ...avatarGrad(0), width: 44, height: 44, borderRadius: 14, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, fontWeight: 800, color: '#fff', flexShrink: 0 }}>
              {avatarChar}
            </div>
            <div>
              <div className="an-modal-title">{primaryText}</div>
              {secondaryText && (
                <div style={{ fontSize: 12.5, color: 'var(--text-muted)', fontWeight: 500, marginTop: 2 }}>
                  {secondaryText}
                </div>
              )}
            </div>
          </div>
          <button className="close-x" onClick={onClose}>×</button>
        </div>

        <div className="an-modal-body" style={{ flexDirection: 'column', overflow: 'auto', padding: '16px 20px', gap: 16 }}>
          {loading ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div className="an-modal-kpi-row">{[0, 1, 2, 3].map(i => <Skeleton key={i} h={80} r={14} />)}</div>
              <Skeleton h={280} r={14} />
            </div>
          ) : error ? (
            <div className="an-empty"><div className="an-empty-title">Error: {error}</div></div>
          ) : (
            <>
              {/* KPI Row */}
              <div className="an-modal-kpi-row">
                {[
                  { label: 'Total Messages', val: fmt(messages.length), color: '#6366f1' },
                  { label: 'Sent by You', val: fmt(outgoing.length), color: '#10b981' },
                  { label: 'Received', val: fmt(incoming.length), color: '#0891b2' },
                  { label: 'Failed', val: fmt(outgoing.filter(m => m.status === 'Failed').length), color: '#ef4444' },
                ].map((k, i) => (
                  <div key={i} className="an-modal-kpi">
                    <div className="an-modal-kpi-val" style={{ color: k.color }}>{k.val}</div>
                    <div className="an-modal-kpi-label">{k.label}</div>
                  </div>
                ))}
              </div>

              {/* Rates */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                {[
                  { label: 'Delivery Rate', rate: dRate, color: '#10b981' },
                  { label: 'Read Rate', rate: rRate, color: '#6366f1' },
                ].map((bar, i) => (
                  <div key={i} style={{ background: 'var(--surface-white)', borderRadius: 14, padding: '16px 18px', border: '1px solid var(--border-color)' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                      <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--text-gray)' }}>{bar.label}</span>
                      <span style={{ fontSize: 14, fontWeight: 800, color: bar.color }}>{bar.rate}%</span>
                    </div>
                    <div className="an-kpi-progress">
                      <div className="an-kpi-progress-fill" style={{ width: `${bar.rate}%`, background: bar.color }} />
                    </div>
                  </div>
                ))}
              </div>

              {/* Message history */}
              <div style={{ background: 'var(--surface-white)', borderRadius: 16, border: '1px solid var(--border-color)', overflow: 'hidden' }}>
                <div style={{ padding: '16px 22px', borderBottom: '1px solid var(--border-color)', display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ fontSize: 13.5, fontWeight: 800, color: 'var(--text-dark)' }}>Message History</span>
                  <span style={{ fontSize: 12, color: 'var(--text-muted)', fontWeight: 600 }}>{messages.length} messages</span>
                </div>
                <div style={{ maxHeight: 360, overflowY: 'auto' }}>
                  {messages.length === 0 ? (
                    <div className="an-empty"><div className="an-empty-title">No messages</div></div>
                  ) : messages.slice().reverse().map((m, i) => (
                    <div key={m.id || i} style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '12px 22px', borderBottom: '1px solid var(--border-color)' }}>
                      <div style={{ width: 30, height: 30, borderRadius: 8, background: m.direction === 'OUTGOING' ? 'rgba(99,102,241,0.1)' : 'rgba(16,185,129,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        {m.direction === 'OUTGOING'
                          ? <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#6366f1" strokeWidth="2"><line x1="22" y1="2" x2="11" y2="13" /><polygon points="22 2 15 22 11 13 2 9 22 2" /></svg>
                          : <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="2"><polyline points="8 17 12 21 16 17" /><line x1="12" y1="12" x2="12" y2="21" /><path d="M20.88 18.09A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.29" /></svg>
                        }
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, color: 'var(--text-dark)', fontWeight: 500, lineHeight: 1.5, wordBreak: 'break-word' }}>
                          {m.text || m.message || '(media)'}
                        </div>
                        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4, display: 'flex', gap: 12 }}>
                          <span>{fmtDate(m.timestamp || m.created_at)}</span>
                          {m.direction === 'OUTGOING' && (
                            <div className={`an-status-badge ${getStatusClass(m.status)}`} style={{ padding: '2px 7px', fontSize: 10 }}>
                              <span className="dot" /> {m.status}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
    </div>,
    document.body
  );
}

// ─── Campaign Metric Detail Modal ───────────────────────────────────────────
function MetricDetailModal({ metric, statsData, onClose }) {
  const [searchTerm, setSearchTerm] = useState('');
  
  useEffect(() => {
    const h = (e) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onClose]);

  const allCampaigns = (statsData?.campaigns?.running || []).filter(c => c.status === 'RUNNING');
  
  const filteredCampaigns = allCampaigns.filter(c => {
    if (metric?.label === 'Messages Sent') return c.count > 0;
    if (metric?.label === 'Delivered') return c.delivered > 0;
    if (metric?.label === 'Messages Read') return c.read > 0;
    if (metric?.label === 'Failed') return c.failed > 0;
    if (metric?.label === 'Active Campaigns') return true;
    if (metric?.label === 'Response Rate') return c.count > 0;
    if (metric?.label === 'Delivery Rate') return c.count > 0;
    if (metric?.label === 'Engagement Score') return c.count > 0;
    return true; 
  }).filter(c => {
    const s = searchTerm.toLowerCase();
    return (c.name || '').toLowerCase().includes(s);
  });

  return createPortal(
    <div className="wa-module">
      <div className="an-modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
        <div className="an-modal" style={{ maxWidth: '800px', width: '90%' }}>
        <div className="an-modal-header" style={{ paddingBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <div style={{ width: 44, height: 44, borderRadius: 14, background: 'var(--brand-purple-light)', color: 'var(--brand-purple)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {metric?.icon}
            </div>
            <div>
              <div style={{ fontSize: 13, color: 'var(--text-muted)', fontWeight: 600 }}>Campaign Analytics</div>
              <div style={{ fontSize: 20, color: 'var(--text-dark)', fontWeight: 800, marginTop: 2 }}>{metric?.label} Details</div>
            </div>
          </div>
          <button className="close-x" onClick={onClose}>×</button>
        </div>
        
        <div className="an-modal-body" style={{ flexDirection: 'column', padding: '0' }}>
          <div style={{ padding: '16px 24px', borderBottom: '1px solid var(--border-color)', background: 'var(--surface-soft-2)' }}>
             <input 
               type="text" 
               placeholder="Search campaigns..." 
               value={searchTerm}
               onChange={e => setSearchTerm(e.target.value)}
               style={{ 
                 width: '100%', 
                 padding: '10px 14px', 
                 borderRadius: '8px', 
                 border: '1px solid var(--border-color)', 
                 background: 'var(--surface-white)',
                 color: 'var(--text-dark)',
                 fontSize: '14px', 
                 outline: 'none' 
               }}
             />
          </div>
          <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
            {!statsData ? (
              <div style={{ padding: '40px', textAlign: 'center' }}>
                <p style={{ color: 'var(--text-gray)' }}>Loading analytics data...</p>
              </div>
            ) : filteredCampaigns.length === 0 ? (
              <div className="an-empty"><div className="an-empty-title">No campaigns have {metric?.label || 'this'} data yet</div></div>
            ) : (
              <div style={{ padding: '12px 24px' }}>
                {filteredCampaigns.map((c, i) => (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 0', borderBottom: '1px solid var(--border-color)' }}>
                    <div>
                      <div style={{ fontWeight: 600, color: 'var(--text-dark)', fontSize: '14px' }}>{c.name || 'Unnamed Campaign'}</div>
                      <div style={{ fontSize: '12px', color: 'var(--text-gray)', marginTop: '4px' }}>Status: <span style={{ color: c.status === 'RUNNING' ? '#10b981' : 'var(--text-gray)', fontWeight: 700 }}>{c.status || 'ACTIVE'}</span></div>
                    </div>
                    <div style={{ display: 'flex', gap: '24px', textAlign: 'right' }}>
                      {(metric?.label === 'Messages Sent' || metric?.label === 'Active Campaigns' || metric?.label === 'Engagement Score' || metric?.label === 'Response Rate') && (
                        <div>
                          <div style={{ fontSize: '12px', color: 'var(--text-gray)', fontWeight: 600 }}>Sent</div>
                          <div style={{ fontWeight: 700, color: '#3b82f6', fontSize: '15px' }}>{fmt(c.count)}</div>
                        </div>
                      )}
                      {(metric?.label === 'Delivered' || metric?.label === 'Active Campaigns' || metric?.label === 'Delivery Rate') && (
                        <div>
                          <div style={{ fontSize: '12px', color: 'var(--text-gray)', fontWeight: 600 }}>Delivered</div>
                          <div style={{ fontWeight: 800, color: '#10b981', fontSize: '15px' }}>{fmt(c.delivered)} <span style={{ fontSize: '11px', opacity: 0.7 }}>({pct(c.delivered, c.count)}%)</span></div>
                        </div>
                      )}
                      {(metric?.label === 'Messages Read' || metric?.label === 'Active Campaigns' || metric?.label === 'Engagement Score') && (
                        <div>
                          <div style={{ fontSize: '12px', color: 'var(--text-gray)', fontWeight: 600 }}>Read</div>
                          <div style={{ fontWeight: 800, color: '#6366f1', fontSize: '15px' }}>{fmt(c.read)} <span style={{ fontSize: '11px', opacity: 0.7 }}>({pct(c.read, c.delivered)}%)</span></div>
                        </div>
                      )}
                      {metric?.label === 'Failed' && (
                        <div>
                          <div style={{ fontSize: '12px', color: 'var(--text-gray)', fontWeight: 600 }}>Failed</div>
                          <div style={{ fontWeight: 800, color: '#ef4444', fontSize: '15px' }}>{fmt(c.failed)} <span style={{ fontSize: '11px', opacity: 0.7 }}>({pct(c.failed, c.count)}%)</span></div>
                        </div>
                      )}
                      {metric?.label === 'Response Rate' && (
                        <div>
                          <div style={{ fontSize: '12px', color: 'var(--text-gray)', fontWeight: 600 }}>Response</div>
                          <div style={{ fontWeight: 800, color: '#f59e0b', fontSize: '15px' }}>N/A</div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
    </div>,
    document.body
  );
}

// ─── Individual Metric Detail Modal ─────────────────────────────────────────
function IndividualMetricDetailModal({ metric, statsData, onClose }) {
  const [searchTerm, setSearchTerm] = useState('');
  
  useEffect(() => {
    const h = (e) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onClose]);

  const allContacts = statsData?.individual?.running || [];
  
  const filteredContacts = allContacts.filter(c => {
    if (metric?.label === 'Messages Sent') return c.count > 0;
    if (metric?.label === 'Delivered') return c.delivered > 0;
    if (metric?.label === 'Read') return c.read > 0;
    if (metric?.label === 'Failed') return c.failed > 0;
    return true; 
  }).filter(c => {
    const s = searchTerm.toLowerCase();
    return (c.name || '').toLowerCase().includes(s) || (c.phone || '').toLowerCase().includes(s);
  });

  return createPortal(
    <div className="wa-module">
      <div className="an-modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
        <div className="an-modal" style={{ maxWidth: '800px', width: '90%' }}>
        <div className="an-modal-header" style={{ paddingBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <div style={{ width: 44, height: 44, borderRadius: 14, background: 'var(--brand-purple-light)', color: 'var(--brand-purple)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {metric?.icon}
            </div>
            <div>
              <div style={{ fontSize: 13, color: 'var(--text-muted)', fontWeight: 600 }}>Conversation Analytics</div>
              <div style={{ fontSize: 20, color: 'var(--text-dark)', fontWeight: 800, marginTop: 2 }}>{metric?.label} Details</div>
            </div>
          </div>
          <button className="close-x" onClick={onClose}>×</button>
        </div>
        
        <div className="an-modal-body" style={{ flexDirection: 'column', padding: '0' }}>
          <div style={{ padding: '16px 24px', borderBottom: '1px solid var(--border-color)', background: 'var(--surface-soft-2)' }}>
             <input 
               type="text" 
               placeholder="Search contacts..." 
               value={searchTerm}
               onChange={e => setSearchTerm(e.target.value)}
               style={{ 
                 width: '100%', 
                 padding: '10px 14px', 
                 borderRadius: '8px', 
                 border: '1px solid var(--border-color)', 
                 background: 'var(--surface-white)',
                 color: 'var(--text-dark)',
                 fontSize: '14px', 
                 outline: 'none' 
               }}
             />
          </div>
          
          <div style={{ maxHeight: '60vh', overflowY: 'auto', overflowX: 'auto', padding: '0' }}>
            {filteredContacts.length === 0 ? (
               <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-gray)' }}>No contacts found for this metric.</div>
            ) : (
               <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
                 <thead style={{ background: 'var(--surface-soft)', position: 'sticky', top: 0, zIndex: 10 }}>
                   <tr>
                     <th style={{ padding: '12px 24px', fontSize: 12, color: 'var(--text-gray)', fontWeight: 600, borderBottom: '1px solid var(--border-color)' }}>Contact</th>
                     <th style={{ padding: '12px 24px', fontSize: 12, color: 'var(--text-gray)', fontWeight: 600, borderBottom: '1px solid var(--border-color)' }}>Received</th>
                     <th style={{ padding: '12px 24px', fontSize: 12, color: 'var(--text-gray)', fontWeight: 600, borderBottom: '1px solid var(--border-color)' }}>Sent</th>
                     <th style={{ padding: '12px 24px', fontSize: 12, color: 'var(--text-gray)', fontWeight: 600, borderBottom: '1px solid var(--border-color)' }}>Delivered</th>
                     <th style={{ padding: '12px 24px', fontSize: 12, color: 'var(--text-gray)', fontWeight: 600, borderBottom: '1px solid var(--border-color)' }}>Read</th>
                     <th style={{ padding: '12px 24px', fontSize: 12, color: 'var(--text-gray)', fontWeight: 600, borderBottom: '1px solid var(--border-color)' }}>Failed</th>
                     <th style={{ padding: '12px 24px', fontSize: 12, color: 'var(--text-gray)', fontWeight: 600, borderBottom: '1px solid var(--border-color)' }}>Last Active</th>
                   </tr>
                 </thead>
                 <tbody>
                   {filteredContacts.map(c => {
                      const rawPhone = c.phone || c.phone_number || '';
                      let cleanPhone = rawPhone.replace(/\D/g, '');
                      if (cleanPhone.length > 10) cleanPhone = cleanPhone.slice(-10);
                      let rawName = c.name && c.name !== 'Unknown' ? c.name : '';
                      let nameDigits = rawName.replace(/\D/g, '');
                      const hasRealName = rawName && nameDigits.length < 8;
                      const primaryText = hasRealName ? rawName : (cleanPhone || rawPhone || 'Unknown');
                      const secondaryText = hasRealName ? (cleanPhone || rawPhone) : '';
                      
                      return (
                     <tr key={c.id} style={{ borderBottom: '1px solid var(--border-color)' }}>
                       <td style={{ padding: '16px 24px' }}>
                          <div style={{ fontWeight: 600, color: 'var(--text-dark)' }}>{primaryText}</div>
                          {secondaryText && <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{secondaryText}</div>}
                       </td>
                       <td style={{ padding: '16px 24px', fontWeight: 600, color: '#0891b2' }}>{c.received || 0}</td>
                       <td style={{ padding: '16px 24px', fontWeight: 600, color: '#3b82f6' }}>{c.count}</td>
                       <td style={{ padding: '16px 24px', fontWeight: 600, color: '#10b981' }}>{c.delivered}</td>
                       <td style={{ padding: '16px 24px', fontWeight: 600, color: '#8b5cf6' }}>{c.read}</td>
                       <td style={{ padding: '16px 24px', fontWeight: 600, color: c.failed > 0 ? '#ef4444' : 'var(--text-gray)' }}>{c.failed}</td>
                       <td style={{ padding: '16px 24px', fontSize: 13, color: 'var(--text-gray)' }}>{c.time}</td>
                     </tr>
                      );
                   })}
                 </tbody>
               </table>
            )}
          </div>
        </div>
      </div>
    </div>
    </div>,
    document.body
  );
}


// ─── Campaign Analytics Section ───────────────────────────────────────────────
function CampaignAnalytics({ statsData, onCardClick, onCampaignClick }) {
  const [searchQuery, setSearchQuery] = useState('');
  
  const s = statsData?.campaigns || {};
  const sent = s.sent || 0;
  const delivered = s.delivered || 0;
  const read = s.read || 0;
  const failed = s.failed || 0;
  const active = s.active || 0;
  const received = s.received || 0;
  const campaigns = (s.running || []).filter(c => c.status === 'RUNNING');

  const filteredCampaigns = campaigns.filter(c => 
    (c.name || '').toLowerCase().includes(searchQuery.toLowerCase())
  );

  const dRate = pct(delivered, sent);
  const rRate = pct(read, delivered);
  const respRate = pct(received, sent);
  const engagement = Math.round(dRate * 0.4 + rRate * 0.6);

  const donutSegments = [
    { label: 'Delivered', value: delivered, color: '#10b981' },
    { label: 'Read', value: read, color: '#6366f1' },
    { label: 'Failed', value: failed, color: '#ef4444' },
    { label: 'Pending', value: Math.max(sent - delivered - failed, 0), color: '#f59e0b' },
  ];

  const funnelSteps = [
    { label: 'Sent', value: sent, pct: sent > 0 ? 100 : 0, color: '#6366f1' },
    { label: 'Delivered', value: delivered, pct: dRate, color: '#10b981' },
    { label: 'Read', value: read, pct: rRate, color: '#0891b2' },
    { label: 'Failed', value: failed, pct: pct(failed, sent), color: '#ef4444' },
  ];

  const kpiCards = [
    { label: 'Active Campaigns', value: fmt(active), icon: iconSend, color: 'purple', trend: `${active}`, trendDir: active > 0 ? 'up' : 'neutral', progress: Math.min(active * 10, 100), spark: mkSpark(active) },
    { label: 'Messages Sent', value: fmt(sent), icon: iconMsg, color: 'blue', trend: fmt(sent), trendDir: sent > 0 ? 'up' : 'neutral', progress: sent > 0 ? 100 : 0, spark: mkSpark(sent) },
    { label: 'Delivered', value: fmt(delivered), icon: iconCheck, color: 'green', trend: `${dRate}%`, trendDir: dRate > 80 ? 'up' : dRate > 50 ? 'neutral' : 'down', progress: dRate, spark: mkSpark(delivered) },
    { label: 'Messages Read', value: fmt(read), icon: iconEye, color: 'purple', trend: `${rRate}%`, trendDir: rRate > 60 ? 'up' : 'neutral', progress: rRate, spark: mkSpark(read) },
    { label: 'Failed', value: fmt(failed), icon: iconX, color: 'red', trend: `${pct(failed, sent)}%`, trendDir: failed > 0 ? 'down' : 'up', progress: pct(failed, sent), spark: mkSpark(failed) },
    { label: 'Response Rate', value: `${respRate}%`, icon: iconReply, color: 'amber', trend: `${respRate}%`, trendDir: respRate > 20 ? 'up' : 'neutral', progress: respRate, spark: mkSpark(received) },
    { label: 'Delivery Rate', value: `${dRate}%`, icon: iconDelivery, color: 'green', trend: dRate > 80 ? '✓ Good' : 'Avg', trendDir: dRate > 80 ? 'up' : 'neutral', progress: dRate, spark: mkSpark(dRate) },
    { label: 'Engagement Score', value: `${engagement}%`, icon: iconStar, color: 'pink', trend: `${engagement}%`, trendDir: engagement > 60 ? 'up' : 'neutral', progress: engagement, spark: mkSpark(engagement) },
  ];

  return (
    <div className="an-section-enter">
      <div className="an-kpi-grid" style={{ marginBottom: 16 }}>
        {kpiCards.map((card, i) => (
          <KpiCard key={i} {...card} onClick={() => onCardClick && onCardClick(card)} />
        ))}
      </div>

      <div className="an-charts-grid" style={{ marginBottom: 16 }}>
        <div className="an-chart-card" style={{ display: 'flex', flexDirection: 'column' }}>
          <div className="an-chart-title" style={{ marginBottom: 12 }}>Message Status</div>
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'flex-start', width: '100%' }}>
            {sent > 0 ? (
              <div className="an-donut-wrap" style={{ width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <DonutChart segments={donutSegments} size={160} />
                <div className="an-donut-labels">
                  {donutSegments.map((seg, i) => (
                    <div key={i} className="an-donut-label-row">
                      <div className="an-legend-dot" style={{ background: seg.color }} />
                      <span>{seg.label}</span>
                      <span className="an-donut-label-val">{fmt(seg.value)}</span>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="an-empty" style={{ padding: '20px 10px' }}>
                <div className="an-empty-title">No data yet</div>
                <div className="an-empty-desc">Send your first campaign to see the breakdown.</div>
              </div>
            )}
          </div>
        </div>

        <div className="an-chart-card">
          <div className="an-chart-title" style={{ marginBottom: 12 }}>Engagement Funnel</div>
          <div className="an-funnel">
            {funnelSteps.map((step, i) => (
              <div key={i} className="an-funnel-row">
                <div className="an-funnel-label">{step.label}</div>
                <div className="an-funnel-bar-wrap">
                  <div className="an-funnel-bar" style={{ background: step.color, width: `${Math.max(step.pct, step.value > 0 ? 4 : 0)}%` }}>
                    {step.value > 0 && <span>{fmt(step.value)}</span>}
                  </div>
                </div>
                <div className="an-funnel-pct">{step.pct}%</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="an-table-card">
        <div className="an-table-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <div className="an-chart-title" style={{ margin: 0 }}>Campaigns — Live Data</div>
            <input 
              type="text" 
              placeholder="Search campaigns..." 
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{ padding: '6px 12px', fontSize: '13px', borderRadius: '6px', border: '1px solid var(--border-color)', outline: 'none', background: 'var(--surface-soft-2)' }}
            />
          </div>
          <span style={{ fontSize: 12, color: 'var(--text-muted)', fontWeight: 600 }}>{filteredCampaigns.length} records</span>
        </div>

        {filteredCampaigns.length === 0 ? (
          <div className="an-empty" style={{ minHeight: '320px' }}>
            <div className="an-empty-icon"><svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M3 11l18-5-5 18-4-8-9-5z" /></svg></div>
            <div className="an-empty-title">No campaigns found</div>
            <div className="an-empty-desc">Your campaign data will appear here once you launch a campaign.</div>
          </div>
        ) : (
          <>
            <div className="an-table-head-row an-table-grid-c">
              <span>Campaign</span><span>Sent</span><span>Delivery %</span><span>Read %</span><span>Status</span><span style={{ textAlign: 'right' }}>Details</span>
            </div>
            <div style={{ height: '320px', overflowY: 'auto' }}>
            {filteredCampaigns.map((camp, i) => {
              const cSent = camp.count || 0;
              const cDel = camp.delivered || 0;
              const cRead = camp.read || 0;
              const cDRate = pct(cDel, cSent);
              const cRRate = pct(cRead, cDel);
              return (
                <div key={camp.id || i} className="an-table-row an-table-grid-c" onClick={() => onCampaignClick && onCampaignClick(camp)}>
                  <div className="an-table-name">
                    <div className="an-table-avatar" style={{ ...avatarGrad(i), color: '#fff' }}>
                      {(camp.name || 'C')[0]}
                    </div>
                    <div>
                      <div>{camp.name || 'Campaign'}</div>
                      <div className="an-table-meta">{camp.time}</div>
                    </div>
                  </div>
                  <div>
                    <div className="an-table-val">{fmt(cSent)}</div>
                    <div className="an-table-sub">messages</div>
                  </div>
                  <div>
                    <div className="an-mini-progress" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <div className="an-mini-bar" style={{ flex: 1, height: '6px', background: '#f1f5f9', borderRadius: '4px', overflow: 'hidden' }}>
                        <div className="an-mini-fill" style={{ height: '100%', width: `${cDRate}%`, background: '#10b981', borderRadius: '4px' }} />
                      </div>
                      <span className="an-table-val" style={{ width: '36px', textAlign: 'right', fontSize: '12px' }}>{cDRate}%</span>
                    </div>
                  </div>
                  <div>
                    <div className="an-mini-progress" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <div className="an-mini-bar" style={{ flex: 1, height: '6px', background: '#f1f5f9', borderRadius: '4px', overflow: 'hidden' }}>
                        <div className="an-mini-fill" style={{ height: '100%', width: `${cRRate}%`, background: '#6366f1', borderRadius: '4px' }} />
                      </div>
                      <span className="an-table-val" style={{ width: '36px', textAlign: 'right', fontSize: '12px' }}>{cRRate}%</span>
                    </div>
                  </div>
                  <div>
                    <div className={`an-status-badge ${camp.status === 'RUNNING' ? 'active' : camp.status === 'COMPLETED' ? 'completed' : camp.status === 'FAILED' ? 'failed' : 'paused'}`}>
                      <span className="dot" /> {camp.status || 'Active'}
                    </div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <button className="an-view-btn" style={{ display: 'inline-flex' }} onClick={e => { e.stopPropagation(); onCampaignClick && onCampaignClick(camp); }}>
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></svg>
                      View
                    </button>
                  </div>
                </div>
              );
            })}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ─── Individual/Conversation Analytics ────────────────────────────────────────
function IndividualAnalytics({ statsData, onContactClick, onCardClick }) {
  const [searchQuery, setSearchQuery] = useState('');
  
  const s = statsData?.individual || {};
  const active = s.active || 0;
  const sentOut = s.sent || 0;
  const delivered = s.delivered || 0;
  const read = s.read || 0;
  const received = s.received || 0;
  const failed = s.failed || 0;
  const conversations = s.running || [];

  const filteredConversations = conversations.filter(c => {
    const s = searchQuery.toLowerCase();
    return (c.name || '').toLowerCase().includes(s) || (c.phone || c.phone_number || '').includes(s);
  });

  const dRate = pct(delivered, sentOut);
  const rRate = pct(read, delivered);
  const replyRate = pct(received, sentOut);
  const engagement = Math.round(dRate * 0.4 + rRate * 0.6);

  const replyTrend = [
    { label: 'Received', values: DAYS.map(() => Math.round(received / 7 * (0.6 + Math.random() * 0.8))) },
    { label: 'Replied', values: DAYS.map(() => Math.round(sentOut / 7 * (0.5 + Math.random() * 0.8))) },
  ];

  // Hourly mock (can be backend-driven later)
  const hourlyData = Array.from({ length: 24 }, (_, h) => {
    const isPeak = (h >= 9 && h <= 12) || (h >= 14 && h <= 18);
    return Math.max(1, Math.round(Math.random() * (isPeak ? 80 : 30) + (isPeak ? 20 : 2)));
  });
  const maxH = Math.max(...hourlyData, 1);

  const liveEvents = conversations.slice(0, 8).map((c, i) => {
    const rawPhone = c.phone || c.phone_number || '';
    let cleanPhone = rawPhone.replace(/\D/g, '');
    if (cleanPhone.length > 10) cleanPhone = cleanPhone.slice(-10);
    let rawName = c.name && c.name !== 'Unknown' ? c.name : '';
    let nameDigits = rawName.replace(/\D/g, '');
    const hasRealName = rawName && nameDigits.length < 8;
    const primaryText = hasRealName ? rawName : (cleanPhone || rawPhone || 'Unknown');
    const secondaryText = hasRealName ? (cleanPhone || rawPhone) : '';
    
    return {
      icon: i % 2 === 0 ? '💬' : '✅',
      iconBg: i % 2 === 0 ? 'rgba(99,102,241,0.1)' : 'rgba(16,185,129,0.1)',
      title: i % 2 === 0 ? 'Customer message received' : 'Message delivered',
      desc: secondaryText ? `${primaryText} · ${secondaryText}` : primaryText,
      time: c.time || '—',
    };
  });

  const kpiCards = [
    { label: 'Contacted Contacts', value: fmt(active), icon: iconChat, color: 'purple', trend: fmt(active), trendDir: active > 0 ? 'up' : 'neutral', progress: Math.min(active * 8, 100), spark: mkSpark(active) },
    { label: 'Messages Received', value: fmt(received), icon: iconDownload, color: 'blue', trend: fmt(received), trendDir: received > 0 ? 'up' : 'neutral', progress: received > 0 ? 100 : 0, spark: mkSpark(received) },
    { label: 'Messages Sent', value: fmt(sentOut), icon: iconSend, color: 'green', trend: fmt(sentOut), trendDir: sentOut > 0 ? 'up' : 'neutral', progress: Math.min(sentOut * 5, 100), spark: mkSpark(sentOut) },
    { label: 'Delivered', value: fmt(delivered), icon: iconCheck, color: 'green', trend: `${dRate}%`, trendDir: dRate > 80 ? 'up' : 'neutral', progress: dRate, spark: mkSpark(delivered) },
    { label: 'Read', value: fmt(read), icon: iconEye, color: 'purple', trend: `${rRate}%`, trendDir: rRate > 50 ? 'up' : 'neutral', progress: rRate, spark: mkSpark(read) },
    { label: 'Reply Rate', value: `${replyRate}%`, icon: iconReply, color: 'amber', trend: `${replyRate}%`, trendDir: replyRate > 20 ? 'up' : 'neutral', progress: replyRate, spark: mkSpark(replyRate) },
    { label: 'Engagement', value: `${engagement}%`, icon: iconStar, color: 'pink', trend: `${engagement}%`, trendDir: engagement > 50 ? 'up' : 'neutral', progress: engagement, spark: mkSpark(engagement) },
    { label: 'Failed', value: fmt(failed), icon: iconX, color: 'red', trend: `${pct(failed, sentOut)}%`, trendDir: failed > 0 ? 'down' : 'up', progress: pct(failed, sentOut), spark: mkSpark(failed) },
  ];

  return (
    <div className="an-section-enter">
      <div className="an-kpi-grid" style={{ marginBottom: 16 }}>
        {kpiCards.map((card, i) => <KpiCard key={i} {...card} onClick={() => onCardClick && onCardClick(card)} />)}
      </div>

      <div className="an-charts-grid" style={{ marginBottom: 20, gridTemplateColumns: '1fr' }}>
        <div className="an-chart-card" style={{ display: 'flex', flexDirection: 'column' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
            <div className="an-chart-title" style={{ margin: 0 }}>Live Activity</div>
            <div className="an-live-dot">Live</div>
          </div>
          {liveEvents.length > 0 ? (
            <div className="an-activity-feed" style={{ flex: 1 }}>
              {liveEvents.map((ev, i) => (
                <div key={i} className="an-feed-item">
                  <div className="an-feed-icon" style={{ background: ev.iconBg }}>{ev.icon}</div>
                  <div className="an-feed-body">
                    <div className="an-feed-title">{ev.title}</div>
                    <div className="an-feed-desc">{ev.desc}</div>
                  </div>
                  <div className="an-feed-time">{ev.time}</div>
                </div>
              ))}
            </div>
          ) : (
            <div className="an-empty" style={{ padding: '20px 10px', flex: 1 }}>
              <div className="an-empty-title">No activity yet</div>
              <div className="an-empty-desc">Conversation events will appear here.</div>
            </div>
          )}
        </div>
      </div>



      {/* Contact Cards */}
      <div className="an-chart-card" style={{ display: 'flex', flexDirection: 'column', marginTop: '16px', padding: '16px 20px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <div className="an-chart-title" style={{ margin: 0 }}>Individual Conversation Insights</div>
            <input 
              type="text" 
              placeholder="Search contacts..." 
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{ padding: '6px 12px', fontSize: '13px', borderRadius: '6px', border: '1px solid var(--border-color)', outline: 'none', background: 'var(--surface-soft-2)' }}
            />
          </div>
          <span style={{ fontSize: 12, color: 'var(--text-muted)', fontWeight: 600 }}>{filteredConversations.length} records</span>
        </div>
        {filteredConversations.length === 0 ? (
          <div className="an-empty" style={{ flex: 1, minHeight: '380px' }}>
            <div className="an-empty-icon"><svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" /></svg></div>
            <div className="an-empty-title">No contacts found</div>
            <div className="an-empty-desc">Individual conversation data will appear here once you start chatting.</div>
          </div>
        ) : (
          <div className="an-customer-list-wrapper" style={{ height: '380px', overflowY: 'auto' }}>
            {filteredConversations.map((c, i) => {
              const cSent = c.count || 0;
              const cDel = c.delivered || 0;
              const cRead = c.read || 0;
              const cDRate = pct(cDel, cSent);
              const score = Math.round(pct(cDel, cSent) * 0.4 + pct(cRead, cDel) * 0.6);
              const scoreClass = score >= 75 ? 'high' : score >= 45 ? 'medium' : 'low';
              const rawPhone = c.phone || c.phone_number || '';
              let cleanPhone = rawPhone.replace(/\D/g, '');
              if (cleanPhone.length > 10) cleanPhone = cleanPhone.slice(-10);
              let rawName = c.name && c.name !== 'Unknown' ? c.name : '';
              let nameDigits = rawName.replace(/\D/g, '');
              const hasRealName = rawName && nameDigits.length < 8;
              const primaryText = hasRealName ? rawName : (cleanPhone || rawPhone || 'Unknown');
              const secondaryText = hasRealName ? (cleanPhone || rawPhone) : '';
              const avatarChar = hasRealName ? rawName.charAt(0).toUpperCase() : (cleanPhone.charAt(0) || '?');

              return (
                <div key={c.id || i} className="an-customer-list-item" onClick={() => onContactClick && onContactClick(c)}>
                  <div className="an-customer-list-left">
                    <div className="an-customer-avatar" style={{ ...avatarGrad(i), color: '#fff' }}>
                      {avatarChar}
                    </div>
                    <div className="an-customer-details">
                      <div className="an-customer-name">{primaryText}</div>
                      {secondaryText && <div className="an-customer-phone">{secondaryText}</div>}
                    </div>
                  </div>
                  
                  <div className="an-customer-list-stats">
                    <div className="an-customer-list-stat">
                      <span className="stat-label">Messages</span>
                      <span className="stat-val">{fmt(cSent)}</span>
                    </div>
                    <div className="an-customer-list-stat">
                      <span className="stat-label">Delivery</span>
                      <span className="stat-val">{cDRate}%</span>
                    </div>
                    <div className="an-customer-list-stat hide-mobile">
                      <span className="stat-label">Last Active</span>
                      <span className="stat-val">{c.time || '—'}</span>
                    </div>
                  </div>

                  <div className="an-customer-list-right">
                    <div className={`an-score-badge ${scoreClass}`}>{score}</div>
                    <div className="an-chevron-indicator">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="9 18 15 12 9 6" /></svg>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Webinar & Reminder Campaign Analytics Component ──────────────────────────────
function WebinarAnalytics() {
  const [analytics, setAnalytics] = useState({
    total_contacts: 0, total_scheduled: 0, total_sent: 0, total_diverted: 0, total_failed: 0,
    delivery_rate: 0, response_rate: 0, attendance_rate: 0,
    counts: { interested: 0, later_dates: 0, not_interested: 0, no_response: 0 },
    contacts: []
  });
  const [campaigns, setCampaigns] = useState([]);
  const [selectedCampaign, setSelectedCampaign] = useState('');
  const [filterCategory, setFilterCategory] = useState('ALL');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);

  const fetchCampaigns = async () => {
    try {
      const res = await apiClient.get('/reminders/');
      setCampaigns(res.data?.results ?? res.data ?? []);
    } catch (e) {
      console.error(e);
    }
  };

  const fetchWebinarAnalytics = async (campId = '') => {
    try {
      setLoading(true);
      const url = campId ? `/reminders/analytics/?campaign_id=${campId}` : '/reminders/analytics/';
      const res = await apiClient.get(url);
      setAnalytics(res.data || {});
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCampaigns();
    fetchWebinarAnalytics();
  }, []);

  const contactsList = (analytics.contacts || []).filter(c => {
    const matchesCat = filterCategory === 'ALL' || c.category === filterCategory;
    const matchesSearch = !search.trim() || 
      (c.contact_name || '').toLowerCase().includes(search.toLowerCase()) || 
      (c.phone_number || '').includes(search);
    return matchesCat && matchesSearch;
  });

  return (
    <div className="an-section-enter" style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
      {/* Selector Bar */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'var(--surface-white)', padding: '16px 20px', borderRadius: '16px', border: '1px solid var(--border-color)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <label style={{ fontSize: '13px', fontWeight: '700', color: 'var(--text-dark)' }}>Webinar / Campaign:</label>
          <select
            value={selectedCampaign}
            onChange={e => {
              const val = e.target.value;
              setSelectedCampaign(val);
              fetchWebinarAnalytics(val);
            }}
            style={{ padding: '8px 14px', borderRadius: '10px', background: 'var(--surface-soft-2)', border: '1px solid var(--border-color)', fontSize: '13px', fontWeight: '600', outline: 'none' }}
          >
            <option value="">-- All Webinars / Campaigns --</option>
            {campaigns.map(c => (
              <option key={c.id} value={c.id}>{c.name} ({c.event_title})</option>
            ))}
          </select>
        </div>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
          <input
            type="text"
            placeholder="Search contact name / phone..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ padding: '8px 14px', borderRadius: '10px', background: 'var(--surface-soft-2)', border: '1px solid var(--border-color)', fontSize: '13px', outline: 'none' }}
          />
          <div style={{ fontSize: '12px', color: '#64748b', fontWeight: '600' }}>
            {analytics.total_contacts || 0} Total Target Leads
          </div>
        </div>
      </div>

      {/* 4 Category Filter Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '14px' }}>
        <button
          type="button"
          onClick={() => setFilterCategory('ALL')}
          style={{
            background: 'var(--surface-white)', borderRadius: '14px', padding: '16px',
            border: `2px solid ${filterCategory === 'ALL' ? 'var(--brand-purple)' : 'var(--border-color)'}`,
            textAlign: 'left', cursor: 'pointer', display: 'flex', flexDirection: 'column', gap: '4px'
          }}
        >
          <div style={{ fontSize: '12px', fontWeight: '700', color: 'var(--brand-purple)' }}>👥 All Enrolled Contacts</div>
          <div style={{ fontSize: '24px', fontWeight: '800', color: 'var(--text-dark)' }}>{analytics.total_contacts || 0}</div>
          <div style={{ fontSize: '11px', color: '#64748b' }}>Total target audience</div>
        </button>

        <button
          type="button"
          onClick={() => setFilterCategory('INTERESTED')}
          style={{
            background: 'var(--surface-white)', borderRadius: '14px', padding: '16px',
            border: `2px solid ${filterCategory === 'INTERESTED' ? '#10b981' : 'var(--border-color)'}`,
            textAlign: 'left', cursor: 'pointer', display: 'flex', flexDirection: 'column', gap: '4px'
          }}
        >
          <div style={{ fontSize: '12px', fontWeight: '700', color: '#059669' }}>🟢 Interested / Attending</div>
          <div style={{ fontSize: '24px', fontWeight: '800', color: '#047857' }}>{(analytics.counts || {}).interested || 0}</div>
          <div style={{ fontSize: '11px', color: '#64748b' }}>Confirmed attendance</div>
        </button>

        <button
          type="button"
          onClick={() => setFilterCategory('LATER_DATES')}
          style={{
            background: 'var(--surface-white)', borderRadius: '14px', padding: '16px',
            border: `2px solid ${filterCategory === 'LATER_DATES' ? '#3b82f6' : 'var(--border-color)'}`,
            textAlign: 'left', cursor: 'pointer', display: 'flex', flexDirection: 'column', gap: '4px'
          }}
        >
          <div style={{ fontSize: '12px', fontWeight: '700', color: '#2563eb' }}>📅 Interested for Later Dates</div>
          <div style={{ fontSize: '24px', fontWeight: '800', color: '#1d4ed8' }}>{(analytics.counts || {}).later_dates || 0}</div>
          <div style={{ fontSize: '11px', color: '#64748b' }}>Triggered follow-up sequence</div>
        </button>

        <button
          type="button"
          onClick={() => setFilterCategory('NOT_INTERESTED')}
          style={{
            background: 'var(--surface-white)', borderRadius: '14px', padding: '16px',
            border: `2px solid ${filterCategory === 'NOT_INTERESTED' ? '#ef4444' : 'var(--border-color)'}`,
            textAlign: 'left', cursor: 'pointer', display: 'flex', flexDirection: 'column', gap: '4px'
          }}
        >
          <div style={{ fontSize: '12px', fontWeight: '700', color: '#dc2626' }}>🔴 Not Interested</div>
          <div style={{ fontSize: '24px', fontWeight: '800', color: '#b91c1c' }}>{(analytics.counts || {}).not_interested || 0}</div>
          <div style={{ fontSize: '11px', color: '#64748b' }}>Diverted / Cancelled</div>
        </button>

        <button
          type="button"
          onClick={() => setFilterCategory('NO_RESPONSE')}
          style={{
            background: 'var(--surface-white)', borderRadius: '14px', padding: '16px',
            border: `2px solid ${filterCategory === 'NO_RESPONSE' ? '#6b7280' : 'var(--border-color)'}`,
            textAlign: 'left', cursor: 'pointer', display: 'flex', flexDirection: 'column', gap: '4px'
          }}
        >
          <div style={{ fontSize: '12px', fontWeight: '700', color: '#4b5563' }}>⏳ No Response Yet</div>
          <div style={{ fontSize: '24px', fontWeight: '800', color: '#374151' }}>{(analytics.counts || {}).no_response || 0}</div>
          <div style={{ fontSize: '11px', color: '#64748b' }}>Awaiting button reply</div>
        </button>
      </div>

      {/* CRM Contacts Data Table */}
      <div style={{ background: 'var(--surface-white)', boxShadow: 'var(--shadow-md)', borderRadius: '16px', border: '1px solid var(--border-color)', overflow: 'hidden' }}>
        {loading ? (
          <div style={{ padding: '40px', textAlign: 'center', color: '#94a3b8' }}>Loading webinar analytics data...</div>
        ) : contactsList.length === 0 ? (
          <div style={{ padding: '40px', textAlign: 'center', color: '#94a3b8' }}>
            No contacts match the selected engagement filter.
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
            <thead>
              <tr style={{ background: 'var(--surface-soft-2)', borderBottom: '1px solid var(--border-color)' }}>
                <th style={{ padding: '16px', color: 'var(--text-gray)', fontWeight: '600' }}>Contact Info</th>
                <th style={{ padding: '16px', color: 'var(--text-gray)', fontWeight: '600' }}>Webinar / Campaign</th>
                <th style={{ padding: '16px', color: 'var(--text-gray)', fontWeight: '600' }}>Engagement Status</th>
                <th style={{ padding: '16px', color: 'var(--text-gray)', fontWeight: '600' }}>Last WhatsApp Reply</th>
                <th style={{ padding: '16px', color: 'var(--text-gray)', fontWeight: '600' }}>Action Triggered</th>
                <th style={{ padding: '16px', color: 'var(--text-gray)', fontWeight: '600' }}>Last Activity</th>
              </tr>
            </thead>
            <tbody>
              {contactsList.map((c, cIdx) => (
                <tr key={cIdx} style={{ borderBottom: '1px solid var(--border-color)' }}>
                  <td style={{ padding: '16px' }}>
                    <div style={{ fontWeight: '700', color: 'var(--text-dark)' }}>{c.contact_name}</div>
                    <div style={{ fontSize: '12px', color: '#64748b' }}>{c.phone_number}</div>
                  </td>
                  <td style={{ padding: '16px', fontSize: '13px', fontWeight: '600', color: '#2563eb' }}>
                    {c.campaign_name}
                  </td>
                  <td style={{ padding: '16px' }}>
                    <span style={{
                      padding: '4px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: '700',
                      display: 'inline-flex', alignItems: 'center', gap: '6px',
                      background: c.category === 'INTERESTED' ? 'rgba(16, 185, 129, 0.15)' :
                                  c.category === 'LATER_DATES' ? 'rgba(59, 130, 246, 0.15)' :
                                  c.category === 'NOT_INTERESTED' ? 'rgba(239, 68, 68, 0.15)' : 'rgba(107, 114, 128, 0.15)',
                      color: c.category === 'INTERESTED' ? '#059669' :
                             c.category === 'LATER_DATES' ? '#2563eb' :
                             c.category === 'NOT_INTERESTED' ? '#dc2626' : '#4b5563'
                    }}>
                      {c.category === 'INTERESTED' && <CheckCircle2 size={13} />}
                      {c.category === 'LATER_DATES' && <Calendar size={13} />}
                      {c.category === 'NOT_INTERESTED' && <XCircle size={13} />}
                      {(c.category === 'NO_RESPONSE' || !c.category) && <Clock size={13} />}
                      {c.category === 'INTERESTED' ? 'Interested' :
                       c.category === 'LATER_DATES' ? 'Later Dates' :
                       c.category === 'NOT_INTERESTED' ? 'Not Interested' : 'No Response'}
                    </span>
                  </td>
                  <td style={{ padding: '16px', fontSize: '13px', fontWeight: '600' }}>
                    "{c.button_response}"
                  </td>
                  <td style={{ padding: '16px', fontSize: '12px', fontWeight: '700', color: 'var(--brand-purple)' }}>
                    {c.action_triggered}
                  </td>
                  <td style={{ padding: '16px', fontSize: '12px', color: '#64748b' }}>
                    {new Date(c.last_updated).toLocaleString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// ─── SVG Icons (inline, tiny) ─────────────────────────────────────────────────
const iconSend = <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 11l18-5-5 18-4-8-9-5z" /></svg>;
const iconMsg = <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="22" y1="2" x2="11" y2="13" /><polygon points="22 2 15 22 11 13 2 9 22 2" /></svg>;
const iconCheck = <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="20 6 9 17 4 12" /></svg>;
const iconEye = <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></svg>;
const iconX = <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /><line x1="15" y1="9" x2="9" y2="15" /><line x1="9" y1="9" x2="15" y2="15" /></svg>;
const iconReply = <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="9 17 4 12 9 7" /><path d="M20 18v-2a4 4 0 0 0-4-4H4" /></svg>;
const iconDelivery = <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" /></svg>;
const iconStar = <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" /></svg>;
const iconChat = <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" /></svg>;
const iconDownload = <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="8 17 12 21 16 17" /><line x1="12" y1="12" x2="12" y2="21" /><path d="M20.88 18.09A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.29" /></svg>;

function mkSpark(base) {
  const b = Math.max(Number(base) || 1, 1);
  return Array.from({ length: 8 }, () => Math.max(1, Math.round(b * (0.6 + Math.random() * 0.8))));
}

// ─── Campaign Filter Dropdown ──────────────────────────────────────────────────
function CampaignFilterDropdown({ campaigns, value, onChange }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef(null);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const filtered = (campaigns || []).filter(c =>
    (c.name || '').toLowerCase().includes(search.toLowerCase())
  );

  const selectedCamp = value ? (campaigns || []).find(c => c.id === value) : null;

  const statusColor = (s) => {
    if (s === 'RUNNING') return '#10b981';
    if (s === 'COMPLETED') return '#6366f1';
    if (s === 'FAILED') return '#ef4444';
    if (s === 'SCHEDULED') return '#f59e0b';
    return '#9ca3af';
  };

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        className="an-campaign-filter-pill"
        onClick={() => { setOpen(v => !v); setSearch(''); }}
        style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
      >
        {/* Funnel icon */}
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
          <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
        </svg>
        <span style={{ maxWidth: '150px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {selectedCamp ? selectedCamp.name : 'All Campaigns'}
        </span>
        {selectedCamp && (
          <span
            onClick={(e) => { e.stopPropagation(); onChange(null); setOpen(false); }}
            style={{ display: 'flex', alignItems: 'center', marginLeft: '2px', opacity: 0.6, cursor: 'pointer' }}
            title="Clear filter"
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </span>
        )}
        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
          style={{ transform: open ? 'rotate(180deg)' : 'rotate(0)', transition: 'transform 0.2s ease', flexShrink: 0 }}>
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      {open && (
        <>
          {/* Backdrop */}
          <div style={{ position: 'fixed', inset: 0, zIndex: 48 }} onClick={() => setOpen(false)} />
          <div className="an-campaign-filter-menu">
            {/* Search */}
            <div style={{ padding: '10px 12px 6px' }}>
              <div style={{ position: 'relative' }}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="var(--text-muted)" strokeWidth="2"
                  style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}>
                  <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
                </svg>
                <input
                  autoFocus
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  placeholder="Search campaigns..."
                  style={{
                    width: '100%', padding: '7px 10px 7px 30px',
                    borderRadius: '8px', border: '1px solid var(--border-color)',
                    background: 'var(--surface-soft)', color: 'var(--text-dark)',
                    fontSize: '12px', outline: 'none', boxSizing: 'border-box'
                  }}
                />
              </div>
            </div>

            {/* Options */}
            <div style={{ maxHeight: '240px', overflowY: 'auto', padding: '4px 8px 8px' }}>
              {/* All Campaigns */}
              <button
                className={`an-campaign-filter-option ${!value ? 'active' : ''}`}
                onClick={() => { onChange(null); setOpen(false); }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#6366f1', flexShrink: 0 }} />
                  <span style={{ fontWeight: 600, fontSize: '12.5px' }}>All Campaigns</span>
                </div>
              </button>

              {filtered.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '16px', color: 'var(--text-muted)', fontSize: '12px' }}>No campaigns found</div>
              ) : filtered.map((c) => (
                <button
                  key={c.id}
                  className={`an-campaign-filter-option ${value === c.id ? 'active' : ''}`}
                  onClick={() => { onChange(c.id); setOpen(false); }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', gap: '8px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
                      <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: statusColor(c.status), flexShrink: 0 }} />
                      <span style={{ fontSize: '12.5px', fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{c.name || 'Unnamed'}</span>
                    </div>
                    <span style={{
                      fontSize: '10px', fontWeight: 700, padding: '2px 6px', borderRadius: '99px',
                      background: `${statusColor(c.status)}18`, color: statusColor(c.status),
                      flexShrink: 0, letterSpacing: '0.03em'
                    }}>{c.status}</span>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ─── Message Pricing: row drill-down modal ─────────────────────────────────────
function PricingDetailModal({ query, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    getMessagePricingDetail(query)
      .then(res => { if (!cancelled) { setData(res); setLoading(false); } })
      .catch(e => { if (!cancelled) { setError(e?.response?.data?.error || e.message || 'Failed to load'); setLoading(false); } });
    return () => { cancelled = true; };
  }, [query]);

  useEffect(() => {
    const h = (e) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onClose]);

  const results = data?.results || [];

  return createPortal(
    <div className="wa-module">
      <div className="an-modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
        <div className="an-modal" style={{ maxWidth: '820px', width: '90%' }}>
        <div className="an-modal-header" style={{ paddingBottom: 16 }}>
          <div>
            <div style={{ fontSize: 13, color: 'var(--text-muted)', fontWeight: 600 }}>Message Pricing</div>
            <div style={{ fontSize: 20, color: 'var(--text-dark)', fontWeight: 800, marginTop: 2 }}>{data?.title || 'Details'}</div>
          </div>
          <button className="close-x" onClick={onClose}>×</button>
        </div>

        <div className="an-modal-body" style={{ flexDirection: 'column', padding: 0 }}>
          {data?.note && (
            <div style={{ padding: '12px 24px', background: 'var(--surface-soft-2)', borderBottom: '1px solid var(--border-color)', fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.5 }}>
              {data.note}
            </div>
          )}
          {loading ? (
            <div style={{ padding: 40 }}><Skeleton h={220} r={14} /></div>
          ) : error ? (
            <div className="an-empty"><div className="an-empty-title">Error: {error}</div></div>
          ) : results.length === 0 ? (
            <div className="an-empty" style={{ minHeight: 200 }}>
              <div className="an-empty-title">No messages match this row</div>
              <div className="an-empty-desc">Nothing to show for the selected filters and date range.</div>
            </div>
          ) : (
            <>
              <div style={{ padding: '10px 24px', fontSize: 12, color: 'var(--text-muted)', fontWeight: 600, borderBottom: '1px solid var(--border-color)' }}>
                Showing {data.shown_count} of {fmt(data.total_count)}
              </div>
              <div style={{ maxHeight: '55vh', overflowY: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
                  <thead style={{ background: 'var(--surface-soft)', position: 'sticky', top: 0 }}>
                    <tr>
                      <th style={{ padding: '10px 24px', fontSize: 12, color: 'var(--text-gray)', fontWeight: 600 }}>Contact</th>
                      <th style={{ padding: '10px 24px', fontSize: 12, color: 'var(--text-gray)', fontWeight: 600 }}>Campaign / Category</th>
                      <th style={{ padding: '10px 24px', fontSize: 12, color: 'var(--text-gray)', fontWeight: 600 }}>Status</th>
                      <th style={{ padding: '10px 24px', fontSize: 12, color: 'var(--text-gray)', fontWeight: 600 }}>Time</th>
                    </tr>
                  </thead>
                  <tbody>
                    {results.map((r, i) => (
                      <tr key={i} style={{ borderBottom: '1px solid var(--border-color)' }}>
                        <td style={{ padding: '12px 24px' }}>
                          <div style={{ fontWeight: 600, color: 'var(--text-dark)' }}>{r.name || r.phone || 'Unknown'}</div>
                          {r.name && <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{r.phone}</div>}
                        </td>
                        <td style={{ padding: '12px 24px', fontSize: 12.5, color: 'var(--text-gray)' }}>
                          {r.campaign_name || (r.direction === 'INCOMING' ? 'Customer reply' : 'Session message')}
                          {r.template_category && <span style={{ marginLeft: 6, fontSize: 10.5, fontWeight: 700, color: 'var(--brand-purple)' }}>({r.template_category})</span>}
                        </td>
                        <td style={{ padding: '12px 24px' }}>
                          <div className={`an-status-badge ${getStatusClass(r.status)}`} style={{ padding: '2px 8px', fontSize: 10.5 }}>
                            <span className="dot" /> {r.status}
                          </div>
                        </td>
                        <td style={{ padding: '12px 24px', fontSize: 12.5, color: 'var(--text-gray)' }}>{fmtDate(r.timestamp)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
    </div>,
    document.body
  );
}

// ─── Message Pricing (WhatsApp Manager style) ─────────────────────────────────
const PRICING_CATEGORY_COLORS = {
  MARKETING: '#6366f1', UTILITY: '#ec4899', AUTHENTICATION: '#f59e0b',
  AUTHENTICATION_INTERNATIONAL: '#64748b', AI_PROVIDER: '#0891b2', SERVICE: '#10b981',
};

function countryName(code) {
  if (!code) return code;
  try {
    return new Intl.DisplayNames(['en'], { type: 'region' }).of(code) || code;
  } catch {
    return code;
  }
}

function PricingCard({ dash, title, total, info, rows, onRowClick }) {
  return (
    <div className="mp-card">
      <div className="mp-card-head">
        <span className="mp-card-dash" style={{ background: dash }} />
        <span className="mp-card-title">{title}</span>
        {info && <span className="mp-info-icon" title={info}>i</span>}
        {total !== undefined && <span className="mp-card-total">{total}</span>}
      </div>
      <div className="mp-card-rows">
        {rows.map((r, i) => (
          <div
            key={i}
            className={`mp-row ${onRowClick ? 'clickable' : ''}`}
            onClick={() => onRowClick && onRowClick(r)}
          >
            <span className="mp-row-dash" style={{ background: r.color || '#94a3b8' }} />
            <span className="mp-row-label">{r.label}</span>
            {r.info && <span className="mp-info-icon" title={r.info}>i</span>}
            <span className="mp-row-value">{r.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function MessagePricingView({ dateRange, setDateRange, customRange, setCustomRange }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [phoneFilter, setPhoneFilter] = useState('');
  const [countryFilter, setCountryFilter] = useState('');
  const [showDateMenu, setShowDateMenu] = useState(false);
  const [drillDown, setDrillDown] = useState(null);

  const dateRangeLabel = (() => {
    switch (dateRange) {
      case 'today': return 'Today';
      case 'yesterday': return 'Yesterday';
      case 'last_7_days': return 'Last 7 Days';
      case 'last_30_days': return 'Last 30 Days';
      case 'custom': return customRange.start && customRange.end ? `${customRange.start} to ${customRange.end}` : 'Custom Range';
      default: return 'Last 90 days';
    }
  })();

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { range: dateRange };
      if (dateRange === 'custom') {
        if (!customRange.start || !customRange.end) { setLoading(false); return; }
        params.start_date = customRange.start;
        params.end_date = customRange.end;
      }
      if (phoneFilter) params.phone_number_id = phoneFilter;
      if (countryFilter) params.country = countryFilter;
      const res = await getMessagePricingStats(params);
      setData(res);
    } catch (e) {
      setError(e?.response?.data?.error || e.message || 'Failed to load pricing insights');
    } finally {
      setLoading(false);
    }
  }, [dateRange, customRange.start, customRange.end, phoneFilter, countryFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openDetail = (extra) => {
    const params = { range: dateRange, ...extra };
    if (dateRange === 'custom') {
      params.start_date = customRange.start;
      params.end_date = customRange.end;
    }
    if (phoneFilter) params.phone_number_id = phoneFilter;
    setDrillDown(params);
  };

  const exportCsv = () => {
    if (!data) return;
    const lines = [['Section', 'Item', 'Count', 'Amount']];
    lines.push(['All Messages', 'Messages sent', data.message_status.sent, '']);
    lines.push(['All Messages', 'Messages delivered', data.message_status.delivered, '']);
    lines.push(['All Messages', 'Messages received', data.message_status.received, '']);
    data.messages_delivered.by_category.forEach(c => lines.push(['Messages Delivered', c.label, c.count, '']));
    data.free_messages_delivered.by_type.forEach(t => lines.push(['Free Messages Delivered', t.label, t.count, '']));
    data.paid_messages_delivered.by_category.forEach(c => { if (c.category !== 'SERVICE') lines.push(['Paid Messages Delivered', c.label, c.count, '']); });
    data.total_charges.by_category.forEach(c => { if (c.category !== 'SERVICE') lines.push(['Approximate Total Charges', c.label, '', `${data.currency} ${c.amount}`]); });
    const csv = lines.map(row => row.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `message-pricing_${data.range.start}_to_${data.range.end}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (error) {
    return (
      <div className="an-empty" style={{ minHeight: 320 }}>
        <div className="an-empty-title">Couldn't load pricing insights</div>
        <div className="an-empty-desc">{error}</div>
      </div>
    );
  }

  return (
    <div className="an-section-enter">
      <div className="mp-filter-bar">
        <select className="mp-select" value={phoneFilter} onChange={e => setPhoneFilter(e.target.value)}>
          <option value="">All phone numbers</option>
          {(data?.phone_numbers || []).map(p => (
            <option key={p.id} value={p.id}>{p.display_phone_number} {p.verified_name ? `(${p.verified_name})` : ''}</option>
          ))}
        </select>
        <select className="mp-select" value={countryFilter} onChange={e => setCountryFilter(e.target.value)}>
          <option value="">All countries</option>
          {(data?.countries || []).map(c => (
            <option key={c} value={c}>{countryName(c)}</option>
          ))}
        </select>
        <div style={{ position: 'relative' }}>
          <button onClick={() => setShowDateMenu(v => !v)} className="an-date-pill">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2" ry="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></svg> {dateRangeLabel}
          </button>
          {showDateMenu && (
            <>
              <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 40 }} onClick={(e) => { e.stopPropagation(); setShowDateMenu(false); }} />
              <div className="an-date-menu" style={{ marginTop: 8, zIndex: 50, display: 'flex', flexDirection: 'column', gap: 4 }}>
                {['today', 'yesterday', 'last_7_days', 'last_30_days', 'all_time', 'custom'].map(range => (
                  <button
                    type="button" key={range}
                    onClick={(e) => { e.stopPropagation(); setDateRange(range); if (range !== 'custom') setShowDateMenu(false); }}
                    className={`an-date-option ${dateRange === range ? 'active' : ''}`}
                  >
                    {range === 'today' ? 'Today' : range === 'yesterday' ? 'Yesterday' : range === 'last_7_days' ? 'Last 7 Days' : range === 'last_30_days' ? 'Last 30 Days' : range === 'custom' ? 'Custom Range' : 'Last 90 Days'}
                  </button>
                ))}
                {dateRange === 'custom' && (
                  <div className="an-date-custom" style={{ padding: 4, borderTop: '1px solid var(--border-color)', marginTop: 4 }} onClick={e => e.stopPropagation()}>
                    <input type="date" value={customRange.start} onChange={e => setCustomRange(r => ({ ...r, start: e.target.value }))} />
                    <input type="date" value={customRange.end} onChange={e => setCustomRange(r => ({ ...r, end: e.target.value }))} />
                    <button onClick={() => setShowDateMenu(false)} style={{ background: 'var(--brand-purple)', color: '#fff', border: 'none', padding: 8, borderRadius: 10, fontSize: 13, fontWeight: 600, cursor: 'pointer', marginTop: 4 }}>Apply Filter</button>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
        <button className="mp-export-btn" onClick={exportCsv} disabled={!data}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" /></svg> Export
        </button>
      </div>

      <div className="mp-note-banner">
        <strong>Note:</strong> All insights data is approximate and may differ from what's shown on your invoices due to small variations in data processing.
      </div>

      {loading || !data ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div className="mp-grid-3">{[0, 1, 2].map(i => <Skeleton key={i} h={220} r={16} />)}</div>
          <div className="mp-grid-2">{[0, 1].map(i => <Skeleton key={i} h={200} r={16} />)}</div>
        </div>
      ) : (
        <>
          <div className="mp-grid-3">
            <PricingCard
              dash="#6366f1"
              title="All Messages"
              rows={[
                { label: 'Messages sent', value: fmt(data.message_status.sent), color: '#6366f1', query: { metric: 'sent' }, info: "Sourced from Meta's own delivery report — accurate even if your local message log lags behind." },
                { label: 'Messages delivered', value: fmt(data.message_status.delivered), color: '#10b981', query: { metric: 'delivered' }, info: "Sourced from Meta's own delivery report — accurate even if your local message log lags behind." },
                { label: 'Messages received', value: fmt(data.message_status.received), color: '#0ea5e9', query: { metric: 'received' }, info: "From your own conversation log — Meta doesn't expose a received-message count, so this one can't be cross-checked against their report." },
              ]}
              onRowClick={(r) => openDetail(r.query)}
            />
            <PricingCard
              dash="#10b981"
              title="Messages Delivered"
              info="Delivered messages broken down by WhatsApp pricing category."
              total={fmt(data.messages_delivered.total)}
              rows={data.messages_delivered.by_category.map(c => ({
                label: c.label, value: fmt(c.count), color: PRICING_CATEGORY_COLORS[c.category],
                query: { metric: 'category', category: c.category },
              }))}
              onRowClick={(r) => openDetail(r.query)}
            />
            <PricingCard
              dash="#0891b2"
              title="Free Messages Delivered"
              info="Messages delivered at no cost — free customer-service replies and free entry-point conversations."
              total={fmt(data.free_messages_delivered.total)}
              rows={data.free_messages_delivered.by_type.map(t => ({
                label: t.label, value: fmt(t.count), color: t.type === 'FREE_CUSTOMER_SERVICE' ? '#10b981' : '#f59e0b',
                query: { metric: 'free', free_type: t.type },
              }))}
              onRowClick={(r) => openDetail(r.query)}
            />
          </div>

          <div className="mp-grid-2">
            <PricingCard
              dash="#ef4444"
              title="Paid Messages Delivered"
              info="Delivered messages billed under WhatsApp's per-message pricing, by category."
              total={fmt(data.paid_messages_delivered.total)}
              rows={data.paid_messages_delivered.by_category.filter(c => c.category !== 'SERVICE').map(c => ({
                label: c.label, value: fmt(c.count), color: PRICING_CATEGORY_COLORS[c.category],
                query: { metric: 'paid', category: c.category },
              }))}
              onRowClick={(r) => openDetail(r.query)}
            />
            <PricingCard
              dash="#f59e0b"
              title="Approximate Total Charges"
              info="Estimated spend for this period, in your account currency."
              total={`${data.currency === 'INR' ? '₹' : data.currency + ' '}${data.total_charges.total}`}
              rows={data.total_charges.by_category.filter(c => c.category !== 'SERVICE').map(c => ({
                label: c.label, value: `${data.currency === 'INR' ? '₹' : data.currency + ' '}${c.amount}`, color: PRICING_CATEGORY_COLORS[c.category],
                query: { metric: 'charge', category: c.category },
              }))}
              onRowClick={(r) => openDetail(r.query)}
            />
          </div>
        </>
      )}

      {drillDown && <PricingDetailModal query={drillDown} onClose={() => setDrillDown(null)} />}
    </div>
  );
}

// ─── "Calling pricing" placeholder ─────────────────────────────────────────────
function CallingPricingView() {
  return (
    <div className="an-empty" style={{ minHeight: 360 }}>
      <div className="an-empty-icon"><svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" /></svg></div>
      <div className="an-empty-title">WhatsApp Calling isn't enabled for this account</div>
      <div className="an-empty-desc">Calling pricing insights will appear here once WhatsApp Business Calling is turned on for this number.</div>
    </div>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────
const AnalyticsView = ({
  stats: propStats,
  setDetailedView,
  activeTab: propActiveTab,
  setActiveTab: propSetActiveTab,
  selectedCampaignFilter: propSelectedCampaignFilter,
  setSelectedCampaignFilter: propSetSelectedCampaignFilter,
  dateRange: propDateRange,
  setDateRange: propSetDateRange,
  customRange: propCustomRange,
  setCustomRange: propSetCustomRange,
}) => {
  // Local state fallbacks if props are not provided (standalone routing mode)
  const [localActiveTab, localSetActiveTab] = useState('campaign');
  const [localSelectedCampaignFilter, localSetSelectedCampaignFilter] = useState(null);
  const [localDateRange, localSetDateRange] = useState('all_time');
  const [localCustomRange, localSetCustomRange] = useState({ start: '', end: '' });

  // Resolve state values (use props if defined, otherwise local state)
  const activeTab = propActiveTab !== undefined ? propActiveTab : localActiveTab;
  const setActiveTab = propSetActiveTab || localSetActiveTab;

  const selectedCampaignFilter = propSelectedCampaignFilter !== undefined ? propSelectedCampaignFilter : localSelectedCampaignFilter;
  const setSelectedCampaignFilter = propSetSelectedCampaignFilter || localSetSelectedCampaignFilter;

  const dateRange = propDateRange !== undefined ? propDateRange : localDateRange;
  const setDateRange = propSetDateRange || localSetDateRange;

  const customRange = propCustomRange !== undefined ? propCustomRange : localCustomRange;
  const setCustomRange = propSetCustomRange || localSetCustomRange;

  const [liveStats, setLiveStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showDateMenu, setShowDateMenu] = useState(false);
  const [topTab, setTopTab] = useState('performance');
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [selectedContact, setSelectedContact] = useState(null);
  const [selectedMetric, setSelectedMetric] = useState(null);
  const fetchStatsRef = useRef(null);

  // ── Compute filtered stats based on selected campaign ──────────────────────
  const filteredStats = useMemo(() => {
    if (!liveStats) return null;
    if (!selectedCampaignFilter) return liveStats;

    // Find the selected campaign in running list
    const camp = (liveStats.campaigns?.running || []).find(c => c.id === selectedCampaignFilter);
    if (!camp) return liveStats;

    return {
      ...liveStats,
      campaigns: {
        active: camp.status === 'RUNNING' ? 1 : 0,
        sent: camp.count || 0,
        delivered: camp.delivered || 0,
        read: camp.read || 0,
        failed: camp.failed || 0,
        received: camp.received || 0,
        running: [camp],
      }
    };
  }, [liveStats, selectedCampaignFilter]);

  // All campaigns list for dropdown
  const allCampaigns = liveStats?.campaigns?.running || [];

  const dateRangeLabel = (() => {
    switch (dateRange) {
      case 'today': return 'Today';
      case 'yesterday': return 'Yesterday';
      case 'last_7_days': return 'Last 7 Days';
      case 'last_30_days': return 'Last 30 Days';
      case 'custom': return customRange.start && customRange.end ? `${customRange.start} to ${customRange.end}` : 'Custom Range';
      default: return 'All time';
    }
  })();

  const buildStatsParams = useCallback(() => {
    if (dateRange === 'custom') {
      if (!customRange.start || !customRange.end) {
        return null;
      }

      return {
        range: 'custom',
        start_date: customRange.start,
        end_date: customRange.end,
      };
    }

    return { range: dateRange };
  }, [dateRange, customRange.end, customRange.start]);

  // Fetch live stats from backend
  const fetchStats = useCallback(async () => {
    try {
      const params = buildStatsParams();
      if (!params) {
        setLoading(false);
        return;
      }

      setLoading(true);

      const data = await getAnalyticsStats(params);
      setLiveStats(data);
    } catch (e) {
      console.error('Analytics fetch failed:', e);
    } finally {
      setLoading(false);
    }
  }, [buildStatsParams]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  useEffect(() => {
    fetchStatsRef.current = fetchStats;
  }, [fetchStats]);

  return (
    <div className="analytics-page">

      {/* ── Top-level insights tabs (WhatsApp Manager style) ── */}
      <div className="mp-toptabs">
        <button className={`mp-toptab ${topTab === 'performance' ? 'active' : ''}`} onClick={() => setTopTab('performance')}>Performance overview</button>
        <button className={`mp-toptab ${topTab === 'pricing' ? 'active' : ''}`} onClick={() => setTopTab('pricing')}>Message pricing</button>
        <button className={`mp-toptab ${topTab === 'calling' ? 'active' : ''}`} onClick={() => setTopTab('calling')}>Calling pricing</button>
      </div>

      {topTab === 'pricing' && (
        <MessagePricingView dateRange={dateRange} setDateRange={setDateRange} customRange={customRange} setCustomRange={setCustomRange} />
      )}
      {topTab === 'calling' && <CallingPricingView />}

      {topTab === 'performance' && (
      <>
      {/* ── Top bar: Tabs | Campaign Filter | Date Filter ── */}
      <div className="an-topbar">
        {/* Left: Analytics tabs */}
        <div className="an-tab-row">
          <button
            onClick={() => setActiveTab('campaign')}
            className={`an-tab ${activeTab === 'campaign' ? 'active' : ''}`}
          >
            <div className="an-tab-dot purple" /> Campaign Analytics
          </button>
          <button
            onClick={() => setActiveTab('individual')}
            className={`an-tab ${activeTab === 'individual' ? 'active' : ''}`}
          >
            <div className="an-tab-dot green" /> Conversation Analytics
          </button>
          <button
            onClick={() => setActiveTab('webinar')}
            className={`an-tab ${activeTab === 'webinar' ? 'active' : ''}`}
          >
            <div className="an-tab-dot amber" style={{ background: '#f59e0b' }} /> Webinar & Reminder Analytics
          </button>
        </div>

        {/* Right: Campaign filter + Date filter */}
        <div className="an-filter-group">
          {/* Campaign Filter — only visible on Campaign Analytics tab */}
          {activeTab === 'campaign' && (
            <>
              {selectedCampaignFilter && (() => {
                const camp = allCampaigns.find(c => c.id === selectedCampaignFilter);
                return camp ? (
                  <span className="an-filtered-label" style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-gray)', marginRight: '2px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                    Filtered by campaign: <span style={{ color: 'var(--brand-purple)', fontWeight: 700 }}>{camp.name}</span>
                  </span>
                ) : null;
              })()}
              <CampaignFilterDropdown
                campaigns={allCampaigns}
                value={selectedCampaignFilter}
                onChange={setSelectedCampaignFilter}
              />
            </>
          )}

          {/* Date Filter */}
          <div style={{ position: 'relative' }}>
            <button onClick={() => setShowDateMenu(v => !v)} className="an-date-pill">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2" ry="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></svg> {dateRangeLabel}
            </button>
            {showDateMenu && (
              <>
                <div
                  style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 40 }}
                  onClick={(e) => { e.stopPropagation(); setShowDateMenu(false); }}
                />
                <div className="an-date-menu" style={{ marginTop: '8px', zIndex: 50, display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  {['today', 'yesterday', 'last_7_days', 'last_30_days', 'all_time', 'custom'].map(range => (
                    <button
                      type="button"
                      key={range}
                      onClick={(e) => {
                        e.stopPropagation();
                        setDateRange(range);
                        if (range !== 'custom') setShowDateMenu(false);
                      }}
                      className={`an-date-option ${dateRange === range ? 'active' : ''}`}
                    >
                      {range === 'today' ? 'Today' : range === 'yesterday' ? 'Yesterday' : range === 'last_7_days' ? 'Last 7 Days' : range === 'last_30_days' ? 'Last 30 Days' : range === 'custom' ? 'Custom Range' : 'All time'}
                    </button>
                  ))}
                  {dateRange === 'custom' && (
                    <div className="an-date-custom" style={{ padding: '4px', borderTop: '1px solid var(--border-color)', marginTop: '4px' }} onClick={e => e.stopPropagation()}>
                      <input type="date" value={customRange.start} onChange={e => setCustomRange(r => ({ ...r, start: e.target.value }))} />
                      <input type="date" value={customRange.end} onChange={e => setCustomRange(r => ({ ...r, end: e.target.value }))} />
                      <button onClick={() => setShowDateMenu(false)} style={{ background: 'var(--brand-purple)', color: '#fff', border: 'none', padding: '8px', borderRadius: '10px', fontSize: '13px', fontWeight: 600, cursor: 'pointer', marginTop: '4px', transition: 'background 0.2s' }}>Apply Filter</button>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>



      {/* Modals */}
      {selectedContact && <ContactDetailModal contact={selectedContact} onClose={() => setSelectedContact(null)} />}
      {selectedCampaign && <CampaignDetailModal campaign={selectedCampaign} onClose={() => setSelectedCampaign(null)} />}
      {selectedMetric && activeTab === 'campaign' && <MetricDetailModal metric={selectedMetric} statsData={filteredStats} onClose={() => setSelectedMetric(null)} />}
      {selectedMetric && activeTab === 'individual' && <IndividualMetricDetailModal metric={selectedMetric} statsData={liveStats} onClose={() => setSelectedMetric(null)} />}

      <div>
        {loading && !liveStats ? (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '24px', marginBottom: '24px' }}>
            {[1, 2, 3, 4, 5, 6, 7, 8].map(i => <Skeleton key={i} h={180} r={24} />)}
          </div>
        ) : (
          <>
            {activeTab === 'campaign' ? (
              <CampaignAnalytics statsData={filteredStats || {}} onCampaignClick={c => setSelectedCampaign(c)} onCardClick={card => setSelectedMetric(card)} />
            ) : activeTab === 'webinar' ? (
              <WebinarAnalytics />
            ) : (
              <IndividualAnalytics statsData={liveStats || {}} onContactClick={c => setSelectedContact(c)} onCardClick={card => setSelectedMetric(card)} />
            )}
          </>
        )}
      </div>
      </>
      )}
    </div>
  );
};

export default AnalyticsView;
